import React, { useCallback, useEffect, useState } from 'react'
import { Newspaper as NewsIcon, Loader2, AlertCircle, Trash2, Pencil, Plus, X, Eye, EyeOff, Pin, Download, FileText } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import {
  listNewsAdmin,
  getNewsStats,
  createNews,
  updateNews,
  changeNewsStatus,
  deleteNews,
  NEWS_CATEGORY_KEYS,
  NEWS_CATEGORY_LABELS,
  NEWS_STATUS_LABELS,
  type NewsItem,
  type NewsRequest,
  type NewsCategory,
  type ResourceStatus,
} from '@/services/newsService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'

const STATUS_FILTERS: (ResourceStatus | 'ALL')[] = ['ALL', 'DRAFT', 'PUBLISHED', 'ARCHIVED']
const STATUS_LIST: ResourceStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED']

const newsExportColumns: ExportColumn<NewsItem>[] = [
  { header: 'ID', key: 'id' },
  { header: 'Title', key: 'title' },
  { header: 'Slug', key: 'slug', format: (v) => (v ? String(v) : '') },
  { header: 'Category', key: 'category' },
  { header: 'Status', key: 'status', format: (v) => (v ? String(v) : '') },
  { header: 'Pinned', key: 'isPinned', format: (v) => (v ? 'Yes' : 'No') },
  { header: 'Sort Order', key: 'sortOrder', format: (v) => (v != null ? String(v) : '') },
  { header: 'Source', key: 'source', format: (v) => (v ? String(v) : '') },
  { header: 'URL', key: 'url', format: (v) => (v ? String(v) : '') },
  { header: 'Summary', key: 'summary', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 300) : '') },
  { header: 'Published At', key: 'publishedAt', format: (v) => (v ? new Date(v as string).toLocaleString() : '') },
]

const statusTone: Record<ResourceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-amber-100 text-amber-700',
}

const EMPTY_FORM: NewsRequest = {
  title: '', slug: '', summary: '', sourceOrg: '', externalUrl: '', imageUrl: '',
  category: 'PROGRAMME', isPinned: false, sortOrder: 0, status: 'DRAFT',
}

const NewsManagement = React.memo(() => {
  const [items, setItems] = useState<NewsItem[]>([])
  const [stats, setStats] = useState<{ TOTAL: number; DRAFT: number; PUBLISHED: number; ARCHIVED: number }>({ TOTAL: 0, DRAFT: 0, PUBLISHED: 0, ARCHIVED: 0 })
  const [statusFilter, setStatusFilter] = useState<ResourceStatus | 'ALL'>('ALL')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<NewsItem | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<NewsRequest>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const size = 20

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, s] = await Promise.all([
        listNewsAdmin({ status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined, page, size }),
        getNewsStats(),
      ])
      setItems(data.content); setTotalPages(data.totalPages); setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load news.')
    } finally { setLoading(false) }
  }, [statusFilter, q, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter, q])

  const flash = (tone: 'success' | 'error', msg: string) => { setToast({ tone, msg }); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setShowForm(true) }
  const openEdit = (n: NewsItem) => {
    setEditing(n)
    setForm({
      title: n.title, slug: n.slug, summary: n.summary ?? '', sourceOrg: n.source ?? '',
      externalUrl: n.url ?? '', imageUrl: n.imageUrl ?? '', category: n.category,
      isPinned: n.isPinned, sortOrder: n.sortOrder ?? 0, status: n.status,
    })
    setFormError(null); setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setFormError(null) }

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!form.title.trim()) { setFormError('Title is required.'); return }
    if (!form.category) { setFormError('Category is required.'); return }
    setSaving(true); setFormError(null)
    try {
      const payload: NewsRequest = {
        ...form, title: form.title.trim(), isPinned: Boolean(form.isPinned),
        sortOrder: Number(form.sortOrder) || 0,
        externalUrl: form.externalUrl || undefined, imageUrl: form.imageUrl || undefined,
        sourceOrg: form.sourceOrg || undefined, summary: form.summary || undefined,
      }
      if (editing) { await updateNews(editing.id, payload); flash('success', `Updated “${form.title}”`) }
      else { await createNews(payload); flash('success', `Created “${form.title}”`) }
      closeForm(); await load()
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'Failed to save.')
    } finally { setSaving(false) }
  }

  const changeStatus = async (n: NewsItem, status: ResourceStatus) => {
    setActingId(n.id)
    try { await changeNewsStatus(n.id, status); flash('success', `“${n.title}” → ${NEWS_STATUS_LABELS[status]}`); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  const remove = async (n: NewsItem) => {
    if (!window.confirm(`Delete “${n.title}”? This cannot be undone.`)) return
    setActingId(n.id)
    try { await deleteNews(n.id); flash('success', 'News item deleted'); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="News & Updates" subtitle="Curate agenda-scoped TB programme news. Only add items that reference a verified official source (Central TB Division / Ni-Kshay). Do not fabricate government data, statistics or named individuals." icon={NewsIcon} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-500">{stats.DRAFT}</p><p className="text-sm text-gray-500">Draft</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.PUBLISHED}</p><p className="text-sm text-gray-500">Published</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.ARCHIVED}</p><p className="text-sm text-gray-500">Archived</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : NEWS_STATUS_LABELS[f]}
              </button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title/summary…" className="text-sm border border-gray-200 rounded-md px-3 py-1.5 ml-auto" />
            <GovButton variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Add news</GovButton>
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">News items</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => exportToCSV('news', newsExportColumns, items)} disabled={!items.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"><Download className="w-3.5 h-3.5" /> CSV</button>
              <button onClick={() => exportToPDF('news', 'News Report', newsExportColumns, items)} disabled={!items.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"><FileText className="w-3.5 h-3.5" /> PDF</button>
              <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : items.length === 0 ? (
            <GovEmptyState icon="📰" title="No news items" description="Add your first curated item with a verified official source, or adjust the filter/search." />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((n) => (
                <div key={n.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[n.status ?? 'DRAFT']}`}>{NEWS_STATUS_LABELS[n.status ?? 'DRAFT']}</span>
                        {n.isPinned && <span className="inline-flex items-center text-xs text-teal-600 font-medium"><Pin className="w-3 h-3 mr-0.5" /> Pinned</span>}
                        <span className="text-xs text-gray-400">{NEWS_CATEGORY_LABELS[n.category]}</span>
                      </div>
                      <h4 className="font-medium text-gray-900">{n.title}</h4>
                      {n.summary && <p className="text-sm text-gray-600 line-clamp-2 mt-1">{n.summary}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {n.source ? n.source : ''}
                        {n.sortOrder ? ` · order ${n.sortOrder}` : ''}
                        {n.publishedAt ? ` · ${new Date(n.publishedAt).toLocaleDateString()}` : ''}
                      </p>
                      {n.url && (
                        <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block truncate max-w-full">
                          {n.url}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === n.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      {n.status === 'PUBLISHED'
                        ? <GovButton variant="ghost" size="sm" onClick={() => changeStatus(n, 'ARCHIVED')} icon={<EyeOff className="w-3.5 h-3.5" />}>Archive</GovButton>
                        : <GovButton variant="ghost" size="sm" onClick={() => changeStatus(n, 'PUBLISHED')} icon={<Eye className="w-3.5 h-3.5" />}>Publish</GovButton>}
                      <button onClick={() => openEdit(n)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(n)} disabled={actingId === n.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={closeForm}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editing ? 'Edit news item' : 'Add news item'}</h3>
                  <p className="text-sm text-gray-500 mt-1">Reference a verified official source (e.g. a Central TB Division / Ni-Kshay programme page). Keep news scoped to the TB-elimination agenda. Do not fabricate data.</p>
                </div>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>

              {formError && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {formError}</div>}

              <form onSubmit={save} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-title">Title <span className="text-red-500">*</span></label>
                  <input id="n-title" type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-slug">Slug (optional)</label>
                    <input id="n-slug" type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input" placeholder="auto from title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-cat">Category <span className="text-red-500">*</span></label>
                    <select id="n-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as NewsCategory })} className="select">
                      {NEWS_CATEGORY_KEYS.map((c) => <option key={c} value={c}>{NEWS_CATEGORY_LABELS[c]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-summary">Summary</label>
                  <textarea id="n-summary" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-src">Source / publisher</label>
                    <input id="n-src" type="text" value={form.sourceOrg ?? ''} onChange={(e) => setForm({ ...form, sourceOrg: e.target.value })} className="input" placeholder="e.g. Central TB Division, MoHFW" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-ext">Official source URL</label>
                    <input id="n-ext" type="url" value={form.externalUrl ?? ''} onChange={(e) => setForm({ ...form, externalUrl: e.target.value })} className="input" placeholder="https://tbcindia.mohfw.gov.in/…" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-img">Image URL (optional)</label>
                    <input id="n-img" type="text" value={form.imageUrl ?? ''} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} className="input" placeholder="usually blank" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="n-status">Status</label>
                    <select id="n-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ResourceStatus })} className="select">
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{NEWS_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={Boolean(form.isPinned)} onChange={(e) => setForm({ ...form, isPinned: e.target.checked })} />
                    Pinned (leads the homepage strip)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    Sort order
                    <input type="number" value={form.sortOrder ?? 0} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} className="input w-24" />
                  </label>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <GovButton type="submit" variant="primary" disabled={saving} icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>{saving ? 'Saving…' : 'Save news'}</GovButton>
                  <GovButton variant="ghost" onClick={closeForm}>Cancel</GovButton>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </GovLayout>
  )
})

NewsManagement.displayName = 'NewsManagement'
export default NewsManagement